import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import Tasks from "@/pages/Tasks";
import Employees from "@/pages/Employees";
import Clients from "@/pages/Clients";
import Leads from "@/pages/Leads";
import Reports from "@/pages/Reports";
import Profile from "@/pages/Profile";
import Invoices from "@/pages/Invoices";
import Settings from "@/pages/Settings";
import Portal from "@/pages/Portal";
import Register from "@/pages/Register";
import MyPortal from "@/pages/MyPortal";
import MyBilling from "@/pages/MyBilling";
import ClientBills from "@/pages/ClientBills";
import MyInventory, { ClientInventory } from "@/pages/Inventory";
import MyPurchases, { ClientPurchases } from "@/pages/Purchases";
import MyReports, { ClientReports } from "@/pages/BizReports";
import POS from "@/pages/POS";
import MyBusiness from "@/pages/MyBusiness";
import Ads from "@/pages/Ads";
import ScanBill from "@/pages/ScanBill";
import AcctRules from "@/pages/AcctRules";
import AiSettings from "@/pages/AiSettings";
import Subscription from "@/pages/Subscription";
import License from "@/pages/License";
import MyBranches from "@/pages/MyBranches";
import Performance from "@/pages/Performance";
import Duplicates from "@/pages/Duplicates";
import { MobileTabBar } from "@/components/MobileTabBar";
import { InstallPrompt } from "@/components/InstallPrompt";
import Install from "@/pages/Install";
import Marketing from "@/pages/Marketing";
import Enquiry from "@/pages/Enquiry";
import Duty from "@/pages/Duty";
import HR from "@/pages/HR";
import Kiosk from "@/pages/Kiosk";
import { initNative } from "@/lib/native";
initNative();
import { GraceBanner } from "@/components/GraceBanner";
import { BiometricEnrollPrompt } from "@/components/Biometric";
import { AppLock } from "@/components/AppLock";
import CustomerPortal from "@/pages/CustomerPortal";
import MyKhata, { Ledger } from "@/pages/Khata";
import PublicKhata from "@/pages/PublicKhata";
import { ForgotPassword, ResetPassword } from "@/pages/PasswordReset";
import { PrivacyPolicy, TermsOfService } from "@/pages/Legal";

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function MenuGuard({ menu, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "admin") return children;
  const allowed = [...(user.allowed_menus || ["dashboard", "tasks", "profile"]), "duty"];
  if (!allowed.includes(menu)) return <Navigate to="/" replace />;
  return children;
}

function Guarded({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null)
    return (
      <div className="flex items-center justify-center min-h-screen text-zinc-500 text-sm tracking-[0.2em] uppercase">
        Loading Ops Console…
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  // Clients get sent to their own portal — they can't see the ops console
  if (user.role === "client" && loc.pathname !== "/my-portal") {
    return <Navigate to="/my-business" replace />;
  }
  // Force password change on first login / after admin reset — block everything except /profile
  if (user.must_change_password && loc.pathname !== "/profile") {
    return <Navigate to="/profile" replace />;
  }
  return children;
}

function RedirectIfAuthed({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  const forceLogin = new URLSearchParams(loc.search).get("switch") === "1";
  if (user === null) return null;
  if (user && !forceLogin) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/login" element={<RedirectIfAuthed><Login /></RedirectIfAuthed>} />
      <Route path="/register" element={<RedirectIfAuthed><Register /></RedirectIfAuthed>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/install" element={<Install />} />
      <Route path="/enquiry" element={<Enquiry />} />
      <Route path="/kiosk" element={<Kiosk />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/terms" element={<TermsOfService />} />
      <Route path="/my-portal" element={<MyPortal />} />
      <Route path="/my-billing" element={<MyBilling />} />
      <Route path="/my-inventory" element={<MyInventory />} />
      <Route path="/my-purchases" element={<MyPurchases />} />
      <Route path="/my-reports" element={<MyReports />} />
      <Route path="/pos" element={<POS />} />
      <Route path="/my-business" element={<MyBusiness />} />
      <Route path="/my-branches" element={<MyBranches />} />
      <Route path="/scan" element={<ScanBill />} />
      <Route path="/scan/:id" element={<ScanBill />} />
      <Route path="/scan/rules" element={<AcctRules />} />
      <Route path="/subscription" element={<Subscription />} />
      <Route path="/c/:token" element={<CustomerPortal />} />
      <Route path="/my-khata/*" element={<MyKhata />} />
      <Route path="/k/:token" element={<PublicKhata />} />
      <Route
        path="/"
        element={
          <Guarded>
            <AppShell />
          </Guarded>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="marketing/*" element={<MenuGuard menu="marketing"><Marketing /></MenuGuard>} />
        <Route path="employees" element={<AdminOnly><Employees /></AdminOnly>} />
        <Route path="hr" element={<AdminOnly><HR /></AdminOnly>} />
        <Route path="duty" element={<Duty />} />
        <Route path="clients" element={<MenuGuard menu="clients"><Clients /></MenuGuard>} />
        <Route path="clients/:id/bills" element={<MenuGuard menu="clients"><ClientBills /></MenuGuard>} />
        <Route path="clients/:id/inventory" element={<MenuGuard menu="clients"><ClientInventory /></MenuGuard>} />
        <Route path="clients/:id/purchases" element={<MenuGuard menu="clients"><ClientPurchases /></MenuGuard>} />
        <Route path="clients/:id/reports" element={<MenuGuard menu="clients"><ClientReports /></MenuGuard>} />
        <Route path="ledger/*" element={<MenuGuard menu="ledger"><Ledger /></MenuGuard>} />
        <Route path="leads" element={<MenuGuard menu="leads"><Leads /></MenuGuard>} />
        <Route path="reports" element={<MenuGuard menu="reports"><Reports /></MenuGuard>} />
        <Route path="performance" element={<MenuGuard menu="reports"><Performance /></MenuGuard>} />
        <Route path="duplicates" element={<AdminOnly><Duplicates /></AdminOnly>} />
        <Route path="ads" element={<MenuGuard menu="ads"><Ads /></MenuGuard>} />
        <Route path="ai-settings" element={<AdminOnly><AiSettings /></AdminOnly>} />
        <Route path="license" element={<AdminOnly><License /></AdminOnly>} />
        <Route path="invoices" element={<MenuGuard menu="invoices"><Invoices /></MenuGuard>} />
        <Route path="settings" element={<AdminOnly><Settings /></AdminOnly>} />
        <Route path="profile" element={<Profile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <GraceBanner />
          <AppRoutes />
          <MobileTabBar />
          <InstallPrompt />
          <BiometricEnrollPrompt />
          <AppLock />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
